import React, { useState, useEffect } from "react";
import { TopFrame } from "@/components/dashboard/TopFrame";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import LeaderboardTab from "@/components/finance/LeaderboardTab";
import ForecastAccuracyTab from "@/components/finance/ForecastAccuracyTab";
import GapAnalysisTab from "@/components/finance/GapAnalysisTab";
import BridgePlanTab from "@/components/finance/BridgePlanTab";
import AIAnalysisTab from "@/components/finance/AIAnalysisTab";
import MarginAnalysisTab from "@/components/finance/MarginAnalysisTab";
import DataCheckWrapper from "@/components/finance/DataCheckWrapper";
import { Toaster } from "@/components/ui/toaster";
import { useFinanceTabs } from "@/hooks/useFinanceTabs";

const Finance = () => {
  const [user, setUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState("leaderboard");
  const navigate = useNavigate();
  const { config: financeConfig, isLoading: configLoading } = useFinanceTabs();
  
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const {
          data: {
            user
          },
          error
        } = await supabase.auth.getUser();
        if (error || !user) {
          navigate("/");
          return;
        }
        setUser(user);
      } catch (error) {
        console.error("Error fetching user:", error);
        navigate("/");
      }
    };
    
    fetchUser();
    
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT") {
        navigate("/");
      } else if (session?.user) {
        setUser(session.user);
      } else if (event === "USER_UPDATED") {
        if (session?.user) {
          setUser(session.user);
        }
      }
    });
    
    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  // Filter available tabs based on configuration
  const availableTabs = [
    { value: "leaderboard", label: "Leader Board", component: <LeaderboardTab /> },
    ...(financeConfig.aiAnalysis ? [{ value: "ai-analysis", label: "AI Analysis", component: <AIAnalysisTab /> }] : []),
    ...(financeConfig.trends ? [{ value: "forecast-accuracy", label: "Trends", component: <ForecastAccuracyTab /> }] : []),
    ...(financeConfig.gapAnalysis ? [{ value: "gap-analysis", label: "Gap Analysis", component: <GapAnalysisTab /> }] : []),
    ...(financeConfig.bridgePlan ? [{ value: "bridge-plan", label: "Bridge Plan", component: <BridgePlanTab /> }] : []),
    ...(financeConfig.marginAnalysis ? [{ value: "margin-analysis", label: "Margin Analysis", component: <MarginAnalysisTab /> }] : [])
  ];

  // Check if current tab is still available, if not switch to first available
  if (!configLoading && !availableTabs.some(tab => tab.value === activeTab)) {
    setActiveTab(availableTabs[0]?.value || "leaderboard");
  }
  
  return (
    <div className="min-h-screen bg-secondary flex flex-col">
      <TopFrame user={user} hideProgress={true} />
      
      <div className="flex-1 container mx-auto px-4 py-6">
        <Card className="border-primary/20 bg-secondary min-h-[calc(100vh-12rem)] overflow-auto">
          <CardContent className="p-6 px-[29px] py-[13px]">
            <DataCheckWrapper selectedTab={activeTab} onTabChange={handleTabChange}>
              <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                <TabsList className={`grid w-[960px] justify-center bg-secondary-light/5 border border-primary/20`} style={{ gridTemplateColumns: `repeat(${availableTabs.length}, 1fr)` }}>
                  {availableTabs.map((tab) => (
                    <TabsTrigger 
                      key={tab.value}
                      value={tab.value} 
                      className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black"
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
                
                {availableTabs.map((tab) => (
                  <TabsContent key={tab.value} value={tab.value} className="mt-4">
                    {tab.component}
                  </TabsContent>
                ))}
              </Tabs>
            </DataCheckWrapper>
          </CardContent>
        </Card>
      </div>
      
      <Toaster />
    </div>
  );
};

export default Finance;
